import classNames from 'classnames/bind';
import React, { Fragment, useEffect } from 'react';
import { IPromotion, Route } from 'common/promotion';
import { IItinerary } from 'common/types';
import { sortMethods } from 'contexts/international';
import { TripType } from 'contexts/searchParams';
import PromotionBanner from 'list/common/promotionBanner';
import StatsRecommendedHotels, {
  INTERNATIONAL_CVR_RECOMMENDED_HOTELS_INDEX,
  RecoType,
  TOP_CLK_RECOMMENDED_HOTELS_INDEX,
} from 'list/common/statsRecommendedHotels';
import InlineBanner, { INLINE_BANNER_INDEX } from '../inlineBanner';
import { IInternationalContextResult } from '../types';
import { getFareKeys } from '../utils';
import styles from './index.module.scss';
import ConcurrentItemContainer from './item';
import { isValidArrivalSchedule } from '../utils';

const cx = classNames.bind(styles);

interface IProps {
  fareType: string;
  flightResult: IInternationalContextResult;
  itinerary: IItinerary[];
  promotions: IPromotion[];
  schs: string[][];
  tripType: TripType;
  selectFlights: (
    sch: string,
    fareType: string,
    airline: string,
    labels: string[],
  ) => void;
  setMinFare: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const ConcurrentList = ({
  fareType,
  flightResult,
  itinerary,
  promotions,
  schs,
  tripType,
  selectFlights,
  setMinFare,
}: IProps) => {
  const { fares, schedules = [] } = flightResult;

  const minFarePerPerson = sortMethods.getLowestSchedulePrice(
    fares,
    schs.map(([sch]) => sch),
  );

  useEffect(() => {
    if (minFarePerPerson) setMinFare(minFarePerPerson);
  }, [minFarePerPerson]);

  return (
    <div className={cx('ConcurrentList')}>
      <PromotionBanner
        fareType={fareType}
        itinerary={itinerary}
        route={Route.INTERNATIONAL}
        trip={tripType}
      />
      {schs
        ?.filter(schArr => {
          const [sch] = schArr;
          const fare = fares.get(sch);
          if (!fare) return false;
          const schKeys = sch.split('|');
          if (
            schKeys.length !== schedules.length ||
            schKeys.some((key, index) => !schedules[index][key])
          ) {
            return false;
          }

          // TODO: 추후 서버에서 validation 되도록 국제선 이관 시 적용
          return isValidArrivalSchedule(schedules, schKeys[0], schKeys[1]);
        })
        .map((schArr, i, array) => {
          const [sch, ...otherItems] = schArr;
          const fare = fares.get(sch)!;
          const fareKeys = getFareKeys(fare);
          const [depSch, arrSch] = sch.split('|');
          const [
            { detail: depDetail, journeyTime: depTime },
            { detail: arrDetail, journeyTime: arrTime },
          ] = [schedules[0][depSch], schedules[1][arrSch]];
          return (
            <>
              <Fragment key={sch}>
                <ConcurrentItemContainer
                  fare={fare}
                  fareKeys={fareKeys}
                  flightResult={flightResult}
                  journeyTimes={[depTime, arrTime]}
                  nClickKey="isb"
                  otherItems={otherItems}
                  promotions={promotions}
                  sch={sch}
                  schedule={[depDetail, arrDetail]}
                  selectFlights={selectFlights}
                />
              </Fragment>
              <Fragment key="banners">
                {(i === TOP_CLK_RECOMMENDED_HOTELS_INDEX - 1 ||
                  (array.length < TOP_CLK_RECOMMENDED_HOTELS_INDEX &&
                    i === array.length - 1)) && (
                  <StatsRecommendedHotels
                    areaCode="isr"
                    arrivalAirport={itinerary[0].arrivalAirport}
                    departureDate={itinerary[0].departureDate}
                    recoType={RecoType.TOP_CLK}
                  />
                )}
                {i === INLINE_BANNER_INDEX - 1 && <InlineBanner />}
                {i === INTERNATIONAL_CVR_RECOMMENDED_HOTELS_INDEX - 1 && (
                  <StatsRecommendedHotels
                    areaCode="isr"
                    arrivalAirport={itinerary[0].arrivalAirport}
                    departureDate={itinerary[0].departureDate}
                    recoType={RecoType.CVR}
                  />
                )}
              </Fragment>
            </>
          );
        })}
    </div>
  );
};

export default ConcurrentList;
